if (!customElements.get('kreenuts-newsletter-gift')) {
    customElements.define('kreenuts-newsletter-gift', class extends HTMLElement {
      static get observedAttributes() {
        return [
          'data-has-order-items',
          'data-server-subscribed',
          'data-cart-unlocked',
          'data-gift-added',
          'data-gift-quantity',
          'data-gift-line-key',
        ];
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return;

        queueMicrotask(() => {
          if (!this.isConnected) return;
          this.refreshStateFromDataset();
          this.sync();
        });
      }

      refreshStateFromDataset() {
        this.refreshStateFromDataset();
      }

      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.options = { signal: this.controller.signal };
        this.status = this.querySelector('[data-gift-status]');
        this.hasOrderItems = this.dataset.hasOrderItems === 'true';
        this.giftAdded = this.dataset.giftAdded === 'true';
        this.giftQuantity = Number(this.dataset.giftQuantity || 0);
        this.serverSubscribed = this.dataset.serverSubscribed === 'true';
        this.cartUnlocked = this.dataset.cartUnlocked === 'true';
        this.signupForm = this.querySelector('[data-gift-signup-form]')?.closest('form');
        this.signupEmail = this.querySelector('[data-gift-email]');
        this.signupButton = this.querySelector('[data-gift-signup-button]');
        this.returnTo = this.querySelector('[data-gift-return-to]');

        this.signupForm?.addEventListener('submit', () => {
          this.prepareNativeSignup();
        }, this.options);

        this.querySelectorAll('[data-gift-variant]').forEach((button) => {
          button.addEventListener('click', () => this.addGift(button), this.options);
        });

        document.addEventListener('kreenuts:newsletter-subscribed', () => this.sync(), this.options);
        document.addEventListener('click', (event) => this.handleGiftRemoveIntent(event), this.options);
        this.completeReturnedSignup();
        this.restoreSignupError();
        this.sync();
        if (this.isSubscribed() && !this.cartUnlocked) this.ensureUnlockAttribute();

        try {
          if (sessionStorage.getItem('kreenuts-gift-reselect') === '1') {
            window.setTimeout(() => this.reconcileGiftState(), 250);
          }
        } catch {}

        if (this.giftAdded && this.giftQuantity > 1 && this.dataset.giftLineKey) {
          this.normalizeGiftQuantity();
          return;
        }

        if (this.giftAdded && !this.hasOrderItems && this.dataset.giftLineKey) {
          this.removeInvalidGift();
        }
      }

      handleGiftRemoveIntent(event) {
        const target = event.target instanceof Element ? event.target.closest('[data-newsletter-gift-remove]') : null;
        if (!target) return;

        try {
          sessionStorage.setItem('kreenuts-gift-reselect', '1');
        } catch {}

        window.setTimeout(() => {
          const current = document.querySelector('kreenuts-newsletter-gift');
          if (current && typeof current.reconcileGiftState === 'function') {
            current.reconcileGiftState();
          }
        }, 450);
      }

      async reconcileGiftState() {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(root + 'cart.js', {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          const cart = await response.json();
          if (!response.ok) return;

          const items = Array.isArray(cart?.items) ? cart.items : [];
          const giftItem = items.find((item) => item?.properties?._kreenuts_newsletter_gift === 'true');
          const hasOrderItems = items.some((item) => item?.properties?._kreenuts_newsletter_gift !== 'true' && Number(item?.quantity || 0) > 0);

          this.giftAdded = Boolean(giftItem);
          this.hasOrderItems = hasOrderItems;
          this.dataset.giftAdded = this.giftAdded ? 'true' : 'false';
          this.dataset.hasOrderItems = this.hasOrderItems ? 'true' : 'false';
          this.sync();

          try {
            if (!this.giftAdded) sessionStorage.removeItem('kreenuts-gift-reselect');
          } catch {}
        } catch (error) {
          console.warn('[kreenuts-newsletter-gift]', error);
        }
      }

      async ensureUnlockAttribute() {
        if (this.cartUnlocked || this.dataset.unlockPersisting === 'true') return;
        this.dataset.unlockPersisting = 'true';

        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(root + 'cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              attributes: { _kreenuts_newsletter_unlocked: 'true' },
            }),
          });

          if (!response.ok) throw new Error('Gift unlock persistence failed');
          this.cartUnlocked = true;
          this.dataset.cartUnlocked = 'true';
          this.serverSubscribed = true;
          this.dataset.serverSubscribed = 'true';
          this.sync();
        } catch (error) {
          console.warn('[kreenuts-newsletter-gift]', error);
        } finally {
          this.dataset.unlockPersisting = 'false';
        }
      }

      isSubscribed() {
        if (this.serverSubscribed || this.cartUnlocked) return true;
        try {
          return localStorage.getItem('kreenuts-newsletter-joined') === '1';
        } catch {
          return false;
        }
      }

      sync() {
        const subscribed = this.isSubscribed();
        let active = 'join';

        if (this.giftAdded) active = 'added';
        else if (subscribed && this.hasOrderItems) active = 'unlocked';

        this.querySelectorAll('[data-gift-state]').forEach((state) => {
          state.hidden = state.dataset.giftState !== active;
        });
      }

      prepareNativeSignup() {
        const email = this.signupEmail?.value.trim() || '';
        if (!email || !this.signupEmail?.checkValidity()) return;

        try {
          const returnUrl = new URL(window.location.href);
          returnUrl.hash = 'kreenuts-gift-unlock';
          if (this.returnTo) {
            this.returnTo.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;
          }
          sessionStorage.setItem('kreenuts-gift-signup-pending', '1');
          sessionStorage.setItem('kreenuts-gift-signup-email', email);
          sessionStorage.removeItem('kreenuts-gift-signup-error');
        } catch {}

        if (this.signupButton) {
          this.signupButton.disabled = true;
          this.signupButton.setAttribute('aria-busy', 'true');
        }
        if (this.status) this.status.textContent = '';
      }

      completeReturnedSignup() {
        let pending = false;
        try {
          pending = sessionStorage.getItem('kreenuts-gift-signup-pending') === '1';
        } catch {}

        if (!pending) return;

        const params = new URLSearchParams(window.location.search);
        const returnedFromGiftSignup =
          window.location.hash === '#kreenuts-gift-unlock' ||
          params.get('customer_posted') === 'true';

        if (!returnedFromGiftSignup) return;

        try {
          localStorage.setItem('kreenuts-newsletter-joined', '1');
          sessionStorage.setItem('kreenuts-gift-signup-returned', '1');
        } catch {}

        this.serverSubscribed = true;
        this.dataset.serverSubscribed = 'true';
        this.ensureUnlockAttribute();
        document.dispatchEvent(new CustomEvent('kreenuts:newsletter-subscribed'));

        requestAnimationFrame(() => {
          const drawer = document.querySelector('#cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        });
      }

      restoreSignupError() {
        try {
          if (sessionStorage.getItem('kreenuts-gift-signup-error') !== '1') return;
          sessionStorage.removeItem('kreenuts-gift-signup-error');
          if (this.status) this.status.textContent = this.dataset.signupError || '';
        } catch {}
      }

      sectionIds() {
        return [...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((component) => component.dataset.sectionId)
            .filter(Boolean)
        )];
      }

      async addGift(button) {
        if (!(button instanceof HTMLButtonElement) || button.disabled) return;

        const variantId = Number(button.dataset.giftVariant);
        if (!variantId) return;

        const buttons = this.querySelectorAll('[data-gift-variant]');
        buttons.forEach((giftButton) => { giftButton.disabled = true; });
        if (this.status) this.status.textContent = button.dataset.loadingLabel || '';

        let addedKey = '';

        try {
          const root = window.Shopify?.routes?.root || '/';
          const addResponse = await fetch(root + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: 1,
                properties: { _kreenuts_newsletter_gift: 'true' },
              }],
            }),
          });

          const addData = await addResponse.json();
          if (!addResponse.ok) throw new Error(addData?.description || 'Gift add failed');
          addedKey = addData?.items?.[0]?.key || '';

          const sectionIds = this.sectionIds();
          const updateResponse = await fetch(root + 'cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              discount: this.dataset.discountCode,
              attributes: { _kreenuts_newsletter_unlocked: 'true' },
              sections: sectionIds,
              sections_url: window.location.pathname,
            }),
          });

          const cart = await updateResponse.json();
          if (!updateResponse.ok) throw new Error(cart?.description || 'Gift discount failed');

          const giftLine = cart.items?.find((item) => item.properties?._kreenuts_newsletter_gift === 'true');
          if (!giftLine || giftLine.final_line_price > 0) {
            if (addedKey) await this.removeLine(addedKey);
            throw new Error('Gift discount was not applied');
          }

          this.refreshSections(cart.sections, sectionIds);
        } catch (error) {
          if (addedKey) {
            try { await this.removeLine(addedKey); } catch {}
          }
          buttons.forEach((giftButton) => { giftButton.disabled = false; });
          if (this.status) this.status.textContent = this.dataset.errorMessage || '';
          console.warn('[kreenuts-newsletter-gift]', error);
        }
      }

      async removeLine(lineKey) {
        const root = window.Shopify?.routes?.root || '/';
        const sectionIds = this.sectionIds();
        const response = await fetch(root + 'cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart?.description || 'Gift removal failed');
        this.refreshSections(cart.sections, sectionIds);
      }

      async normalizeGiftQuantity() {
        if (this.dataset.normalizing === 'true') return;
        this.dataset.normalizing = 'true';

        try {
          const root = window.Shopify?.routes?.root || '/';
          const sectionIds = this.sectionIds();
          const response = await fetch(root + 'cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: this.dataset.giftLineKey,
              quantity: 1,
              sections: sectionIds,
              sections_url: window.location.pathname,
            }),
          });
          const cart = await response.json();
          if (!response.ok) throw new Error(cart?.description || 'Gift quantity correction failed');
          this.refreshSections(cart.sections, sectionIds);
        } catch (error) {
          this.dataset.normalizing = 'false';
          console.warn('[kreenuts-newsletter-gift]', error);
        }
      }

      async removeInvalidGift() {
        if (this.dataset.cleaning === 'true') return;
        this.dataset.cleaning = 'true';

        try {
          await this.removeLine(this.dataset.giftLineKey);
        } catch (error) {
          this.dataset.cleaning = 'false';
          console.warn('[kreenuts-newsletter-gift]', error);
        }
      }

      refreshSections(sections, sectionIds) {
        if (!sections || !sectionIds.length) {
          window.location.reload();
          return;
        }

        let updated = false;

        for (const sectionId of sectionIds) {
          const html = sections[sectionId];
          const currentSection = document.getElementById('shopify-section-' + sectionId);
          if (!html || !currentSection) continue;

          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const replacementSection = parsed.getElementById('shopify-section-' + sectionId);
          const currentCart = currentSection.querySelector('cart-items-component');
          const replacementCart = replacementSection?.querySelector('cart-items-component');

          if (currentCart && replacementCart) {
            currentCart.replaceWith(replacementCart);
            updated = true;
          }
        }

        if (!updated) window.location.reload();
      }

      disconnectedCallback() {
        this.controller?.abort();
      }
    });
  }
